/**
 *  @class
 *  @function PredictiveSearch
 */
class PredictiveSearch {
  constructor() {
    this.container = document.getElementById('Search-Drawer');
    if (!this.container) return;
    this.form = this.container.querySelector('form.searchform');
    this.button = document.querySelectorAll('.thb-quick-search');
    this.input = this.container.querySelector('input[type="search"]');
    this.defaultTab = this.container.querySelector('.side-panel-content--initial');
    this.predictiveSearchResults = this.container.querySelector('.side-panel-content--has-tabs');
    if (!this.form || !this.input || !this.predictiveSearchResults) return;
    this.cache = new Map();
    this.controller = null;
    this.requestTimeout = null;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('input', () => {
      if (!this.getQuery()) {
        this.controller?.abort();
        this.predictiveSearchResults.classList.remove('active', 'loading');
      }
    });
    document.addEventListener('panel:closed', event => {
      if (event.detail?.panel !== this.container) return;
      this.controller?.abort();
      this.predictiveSearchResults.classList.remove('loading');
    });
    window.addEventListener('pagehide', () => {
      this.controller?.abort();
      this.predictiveSearchResults.classList.remove('loading');
    });

    this.button.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();
        if (OverlayManager.isOpen(this.container)) {
          OverlayManager.close(this.container);
        } else {
          this.stampPromotionTemplate();
          OverlayManager.open(this.container);
          setTimeout(() => {
            if (!OverlayManager.isOpen(this.container)) return;
            this.input.focus({
              preventScroll: true
            });
          }, 100);
          dispatchCustomEvent('search:open');
        }
      });
    });

    this.predictiveSearchResults.addEventListener('click', (event) => {
      const submitButton = event.target.closest('#search-results-submit');
      if (submitButton) {
        this.form.submit();
      }
    });
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    if (!OverlayManager.isOpen(this.container)) return;
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.controller?.abort();
      this.predictiveSearchResults.classList.remove('active', 'loading');
      return;
    }
    this.predictiveSearchResults.classList.add('active');
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) {
      event.preventDefault();
    }
  }

  getSearchResults(searchTerm) {
    clearTimeout(this.requestTimeout);
    if (this.controller) {
      this.controller.abort();
    }
    this.controller = new AbortController();
    const controller = this.controller;

    // Serve from cache if available
    if (this.cache.has(searchTerm)) {
      this.predictiveSearchResults.classList.remove('loading');
      this.renderSearchResults(this.cache.get(searchTerm));
      return;
    }

    this.predictiveSearchResults.classList.add('loading');

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product,article,query',
      'resources[limit]': '10',
      'resources[options][fields]': 'title,product_type,vendor,variants.title,variants.sku',
      section_id: 'predictive-search'
    });

    let timeout;
    Promise.race([
      fetch(`${theme.routes.predictive_search_url}?${params.toString()}`, { signal: controller.signal }).then((response) => {
        if (!response.ok) {
          throw new Error(response.status);
        }
        return response.text();
      }),
      new Promise((_, reject) => {
        timeout = this.requestTimeout = setTimeout(() => { reject(new Error('Search timed out')); controller.abort(); }, 10000);
        controller.signal.addEventListener('abort', () => { clearTimeout(timeout); reject(new DOMException('Search cancelled', 'AbortError')); }, { once: true });
      })
    ])
      .then((text) => {
        if (controller.signal.aborted || controller !== this.controller || this.getQuery() !== searchTerm || !OverlayManager.isOpen(this.container)) return;
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Search results unavailable');
        const resultsMarkup = section.innerHTML;
        this.predictiveSearchResults.classList.remove('loading');

        // Cap cache size
        if (this.cache.size >= 50) {
          const firstKey = this.cache.keys().next().value;
          this.cache.delete(firstKey);
        }
        this.cache.set(searchTerm, resultsMarkup);

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || controller !== this.controller) {
          return;
        }
        this.predictiveSearchResults.classList.remove('loading');
        this.predictiveSearchResults.classList.remove('active');
      }).finally(() => {
        clearTimeout(timeout);
        if (this.controller === controller) { this.controller = null; this.requestTimeout = null; }
      });
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
  }

  stampPromotionTemplate() {
    const tpl = this.container.querySelector('.thb-predictive-search--promotion-template');
    if (tpl) {
      tpl.replaceWith(tpl.content.cloneNode(true));
    }
  }
}
window.addEventListener('load', () => {
  if (typeof PredictiveSearch !== 'undefined') {
    new PredictiveSearch();
  }
});
